import { Container, Sprite, Graphics, Text, TextStyle, Assets } from 'pixi.js';
import ConfettiEffect from './confettiEffect';
import TrailEffect from './TrailEffect';
import ImpactEffect from './ImpactEffect';
import PowerUpSystem, { PowerUpType } from './PowerUpSystem';
import GoldRingManager from './GoldRing';
import GoldStarManager from './GoldStar';
import FloatingScoreNumbers from './FloatingScoreNumbers';
import PortalManager from './PortalManager';
import { BALL_RADIUS, CANVAS_HEIGHT, FLOOR_HEIGHT, PLAYABLE_AREA, WALL_THICKNESS, SPECIAL_PLATFORM_IDS } from './constants';
import { calculateForce, cameraShake, checkIfMobile } from './utils';
import defaultSettings from './defaultSettings';
import { BALL_UPDATE_RATE, BallUpdate, GameMode, PlayerId, RoomState, Time } from '../shared/sharedTypes';
import { advancedInterpolation } from './AdvancedInterpolation';
import SocketClient from './socket-client';
import GameStateManager from './gameStateManager';
import UpgradeSystem from './UpgradeSystem';
import UpgradeUI from './UpgradeUI';
import { GameModeHandler } from './gamemodes/GameModeHandler';
import { CampaignMode } from './gamemodes/CampaignMode';
import { snakeupMode } from './gamemodes/snakeupMode';
import { CoinDashMode } from './gamemodes/CoinDashMode';
import { BaseGameMode } from './gamemodes/BaseGameMode'; // Fallback

type BallState = 'normal' | 'aiming' | 'idle';

interface BallData {
    position: { x: number; y: number };
    velocity: { x: number; y: number };
    angle: number;
    graphics: Container;
    nameText: Text;
    ballState: BallState;
    ballBody: Graphics;
    eyesContainer: Container;
    blushLeft: Graphics;
    blushRight: Graphics;
    sweatDrop: Graphics;
}

interface OtherBallData {
    graphics: Container;
    trailGraphics: Graphics;
    trailPoints: { x: number; y: number }[];
    nameText: Text;
}

interface PositionData {
    previous: { x: number; y: number };
    target: { x: number; y: number };
    lerpTime: number;
}

export default class BallManager {
    private gameWorld: Container;
    private socketClient: SocketClient;
    private platformManager: any;
    private app: any;
    private viewportManager: any;
    private gameStateManager: GameStateManager;
    private roomState: RoomState;
    private soundManager: any;
    private chat: any;
    private matterWorker: Worker;
    private isMobile: boolean = false;

    private ball: BallData | null = null;
    private trailPoints: { x: number; y: number }[] = [];
    private trailGraphics: Graphics;
    private otherBalls: { [key: string]: OtherBallData } = {};
    private previousAndTargetPositions: { [key: string]: PositionData } = {};
    public inMotion: boolean = false;
    private hasLeftTheGroundSinceLastStrike: boolean = false;
    private colliding: boolean = false;
    private initialPosition: { x: number; y: number } | null = null;
    private highestHeight: number = 0;
    private localBallId: string;
    private confettiEffect: ConfettiEffect;
    private trailEffect: TrailEffect;
    private impactEffect: ImpactEffect;
    public powerUpSystem: PowerUpSystem;
    public goldRingManager: GoldRingManager;
    public goldStarManager: GoldStarManager;
    private floatingScoreNumbers: FloatingScoreNumbers;
    public portalManager: PortalManager;
    private lastPortalTeleportTime: number = 0;
    private readonly PORTAL_COOLDOWN: number = 500; // 500ms cooldown to prevent rapid teleporting
    private ringGraphics: Graphics;
    private extraJumpsRemaining: number = 0;
    private pulsationTime: number = 0;
    private lastUpdate: number = 0;
    private lastStrikeTime: number = 0;
    private isMouseButtonDown: boolean = false;
    private lastStrikePosition: { x: number, y: number } | null = null;

    private previouslyHittable: boolean = false;

    private lastPowerUpUseTime: number = 0;
    private readonly POWER_UP_COOLDOWN: number = 20000; // 20 seconds
    
    // Combo system
    private comboCount: number = 0;
    private lastHitTime: number = 0;
    private readonly COMBO_TIMEOUT: number = 2000; // 2 seconds
    private readonly COMBO_THRESHOLD: number = 3; // Number of hits needed to earn a double jump
    private comboDoubleJumpAvailable: boolean = false;
    private comboGraphics: Graphics;
    private comboText: Text | null = null;
    private debugJumpText: Text;

    public upgradeSystem: UpgradeSystem;
    private upgradeUI: UpgradeUI;
    
    public gameModeHandler: GameModeHandler;

    private ballState: BallState = 'normal';
    private eyeLookTime: number = 0;

    private renderManager: any;

    constructor(
        bootstrapRoomState: RoomState,
        matterWorker: Worker,
        gameWorld: Container,
        socketClient: any,
        platformManager: any,
        app: any,
        gameStateManager: any,
        chat: any,
        viewportManager: any,
        soundManager: any,
        renderManager?: any
    ) {
        advancedInterpolation.setInterpolationMethod(false);
        advancedInterpolation.setSmoothingFactor(1); // Adjust as needed
        this.isMobile = checkIfMobile();
        this.matterWorker = matterWorker;
        this.gameWorld = gameWorld;
        this.socketClient = socketClient;
        this.socketClient.handleWebRTCBallUpdate = this.updateOtherPlayerBall.bind(this);
        this.setupListeners();

        this.platformManager = platformManager;
        this.app = app;
        this.viewportManager = viewportManager;
        this.gameStateManager = gameStateManager;
        this.roomState = bootstrapRoomState;
        this.soundManager = soundManager;
        this.chat = chat;
        this.renderManager = renderManager;

        // Initialize Game Mode Handler
        // In a real implementation, this might come from a factory or main.ts
        if (this.roomState.roomSettings?.gameType === 'campaign') {
            this.gameModeHandler = new CampaignMode(this, this.roomState);
        } else if (this.roomState.roomSettings?.gameMode === 'coinDash') {
            this.gameModeHandler = new CoinDashMode(this, this.roomState);
        } else if (this.roomState.roomSettings?.gameMode === 'snakeup') { 
             this.gameModeHandler = new snakeupMode(this, this.roomState);
        } else {
            // Check for other modes or default
            // TODO: Add Snake Mode check once we have a GameMode enum for it
            // Create a concrete class that extends BaseGameMode just for this fallback
            class ClassicGameMode extends BaseGameMode {
                getModeName() { return "Classic"; }
            }
            this.gameModeHandler = new ClassicGameMode(this, this.roomState);
        }
        // Store canvas height before game mode init
        const canvasHeightBefore = CANVAS_HEIGHT;
        
        this.gameModeHandler.init();

        // Recalculate viewport, initial scroll, world bounds, and green hill if canvas height changed (e.g., snakeup mode doubles it)
        // Note: Height ruler UI will be updated automatically in uiManager.update() when it detects the change
        if (CANVAS_HEIGHT !== canvasHeightBefore) {
            if (this.viewportManager) {
                this.viewportManager.adjustViewport();
                // Recalculate initial scroll to bottom with the new canvas height
                this.viewportManager.recalculateInitialScroll();
            }
            // Recreate world bounds (floor, ceiling, walls) with the new canvas height
            if (this.platformManager && this.platformManager.recreateWorldBounds) {
                this.platformManager.recreateWorldBounds();
            }
            // Update green hill position with new canvas height
            if (this.renderManager && this.renderManager.updateGreenHill) {
                console.log('[BallManager] Canvas height changed, updating green hill. Old:', canvasHeightBefore, 'New:', CANVAS_HEIGHT);
                this.renderManager.updateGreenHill();
            } else {
                console.warn('[BallManager] renderManager or updateGreenHill not available');
            }
        }

        // Apply Game Mode Physics Settings
        const physicsSettings = this.gameModeHandler.getPhysicsSettings();
        if (physicsSettings) {
            this.matterWorker.postMessage({
                type: 'updatePhysicsSettings',
                gravity: physicsSettings.gravity
            });
        }

        this.trailGraphics = new Graphics();
        this.gameWorld.addChild(this.trailGraphics);

        this.localBallId = this.socketClient.socket.id;
        this.confettiEffect = new ConfettiEffect(this.app);
        
        // Initialize particle trail effect
        const trailContainer = new Container();
        this.gameWorld.addChild(trailContainer);
        this.trailEffect = new TrailEffect(this.app, trailContainer);

        // Initialize impact effects
        this.impactEffect = new ImpactEffect(this.app, this.gameWorld);
        
        // Initialize power-up system and collectibles
        this.powerUpSystem = new PowerUpSystem(this.gameWorld);
        this.goldRingManager = new GoldRingManager(this.app, this.gameWorld);
        this.goldStarManager = new GoldStarManager(this.app, this.gameWorld);
        this.floatingScoreNumbers = new FloatingScoreNumbers(this.gameWorld);
        this.portalManager = new PortalManager(this.app, this.gameWorld);

        this.ringGraphics = new Graphics();
        this.gameWorld.addChild(this.ringGraphics);
        
        // Initialize combo system
        this.comboGraphics = new Graphics();
        this.gameWorld.addChild(this.comboGraphics);

        this.debugJumpText = new Text({ text: '', style: { fill: 0xFF0000, fontSize: 16 } });
        this.app.stage.addChild(this.debugJumpText);
        this.debugJumpText.x = 10;
        this.debugJumpText.y = this.app.renderer.height - 120;
        this.debugJumpText.visible = false; // Hidden - using new design system

        this.upgradeSystem = new UpgradeSystem(this);
        this.upgradeUI = new UpgradeUI(this.app, (upgrade) => {
            this.upgradeSystem.selectUpgrade(upgrade);
            this.soundManager.playPowerUpSound();
            
            // Log Analytics
            this.socketClient.sendAnalyticsEvent('upgrade_selected', { 
                upgradeId: upgrade.id, 
                upgradeName: upgrade.name 
            });

            // Apply immediate physical changes if needed
            this.applyUpgradeEffects();
        });

        this.previouslyHittable = !this.cantHit();

        this.matterWorker.onmessage = this.handleWorkerMessage.bind(this);
        this.setEventListeners();
        setTimeout(() => this.createBall(), 1000);
        // render other initial player balls 
        Object.entries(this.roomState.players).forEach(([id, playerData]: [string, any]) => {
            if (id !== this.socketClient.socket.id) {
                this.createOtherPlayerBall(id, playerData);
            }
        });
    }

    private applyUpgradeEffects(): void {
        if (!this.ball) return;

        const sizeMultiplier = this.upgradeSystem.getSizeMultiplier();
        const newRadius = BALL_RADIUS * sizeMultiplier;
        
        // Recreate ball graphics with new radius
        const oldState = this.ball.ballState;
        const oldPosition = { ...this.ball.position };
        const oldAngle = this.ball.angle;
        
        // Remove old graphics
        this.gameWorld.removeChild(this.ball.graphics);
        
        // Create new ball graphics with updated radius
        const newBallContainer = this.createBallGraphics(newRadius);
        const ballBody = newBallContainer.children[0] as Graphics;
        const eyesContainer = newBallContainer.children[1] as Container;
        const blushLeft = newBallContainer.children[2] as Graphics;
        const blushRight = newBallContainer.children[3] as Graphics;
        const sweatDrop = newBallContainer.children[4] as Graphics;
        
        this.gameWorld.addChild(newBallContainer);
        
        // Update ball data
        this.ball.graphics = newBallContainer;
        this.ball.ballBody = ballBody;
        this.ball.eyesContainer = eyesContainer;
        this.ball.blushLeft = blushLeft;
        this.ball.blushRight = blushRight;
        this.ball.sweatDrop = sweatDrop;
        
        // Restore state
        this.setBallState(oldState);
        
        // Update ring graphics
        this.ringGraphics.clear();
        this.ringGraphics.circle(0, 0, newRadius + (this.isMobile ? 10 : 5));
        this.ringGraphics.stroke({ width: this.isMobile ? 20 : 4, color: 0xFFFFFF, alpha: 0.5 });

        // Update physics body properties
        this.matterWorker.postMessage({
            type: 'updateBallProperties',
            radius: newRadius,
            restitution: 0.8 * this.upgradeSystem.getBouncinessMultiplier()
        });
    }

    private handleWorkerMessage(event: MessageEvent): void {
        switch (event.data.type) {
            case 'ballUpdated':
                this.updateBallState(event.data);
                break;
            case 'collisionResults':
                this.handleCollisionResults(event.data.collisions);
                break;
            case 'ballTouchingPlatform':
                // Update any necessary state based on whether the ball is touching a platform
                break;
            case 'worldUpdated':
                break;
        }
    }

    private updateBallState(data: any): void {
        if (this.ball && data.ball) {
            const targetPosition = data.ball.position;
            const currentPosition = this.ball.position;

            // Smoothly interpolate to the target position
            this.ball.position.x += (targetPosition.x - currentPosition.x) * 0.3;
            this.ball.position.y += (targetPosition.y - currentPosition.y) * 0.3;

            this.ball.velocity = data.ball.velocity;
            this.ball.angle = data.ball.angle;

            this.updateBallGraphics();
            // this.updateTrail();
            this.updateHighestHeight();
            this.updateBallPositionOnSocket();
            
            const stationary = this.isBallStationary();
            const timeSinceStrike = Date.now() - this.lastStrikeTime;

            // Force inMotion to true for a short window after striking to prevent early landing detection
            if (timeSinceStrike < 300) {
                this.inMotion = true;
            } else {
                this.inMotion = !stationary;
            }

            if (!this.inMotion && this.hasLeftTheGroundSinceLastStrike) {
                this.hasLeftTheGroundSinceLastStrike = false;
            }
            this.gameStateManager.ball = this.ball;
        }
    }

    private setEventListeners(): void {
        this.socketClient.socket.on('roundEnded', (data: any) => {
            this.confettiEffect.start();
            this.soundManager.playVictorySound();
            setTimeout(() => {
                this.confettiEffect.stop();
            }, data.timeUntilNextRound);
        });
        this.socketClient.socket.on('platformsUpdated', () => {
            this.resetBallPosition();
        });
        this.socketClient.socket.on('roomStateReceived', this.handleInitialState.bind(this));

        this.socketClient.socket.on('batchedBallUpdates', (updates: { [playerId: string]: BallUpdate }) => {
            Object.entries(updates).forEach(([playerId, update]) => {
                if (playerId !== this.socketClient.socket.id) {
                    this.updateOtherPlayerBall(playerId, update);
                }
            });
        });
    }

    private handleInitialState(state: any): void {
        Object.keys(this.otherBalls).forEach(id => this.removeOtherPlayerBall(id));
        Object.entries(state.players).forEach(([id, playerData]: [string, any]) => {
            if (id !== this.socketClient.socket.id) {
                this.createOtherPlayerBall(id, playerData);
            }
        });
    }

    /**
     * Creates the new design system ball with Graphics
     * Uses smooth rendering for crisp edges
     */
    private createBallGraphics(radius: number, ballColor: number = 0xFFFFFF): Container {
        const container = new Container();
        
        // Main ball body (circle with black border, colored fill)
        // Use higher resolution for smooth rendering
        const ballBody = new Graphics();
        ballBody.circle(0, 0, radius);
        ballBody.fill({ color: ballColor });
        ballBody.stroke({ width: 3, color: 0x000000 });
        // Enable smooth rendering
        ballBody.roundPixels = false;
        container.addChild(ballBody);
        
        // Eyes container
        const eyesContainer = new Container();
        const eyeSize = radius * 0.1;
        const eyeSpacing = radius * 0.3;
        
        // Left eye
        const leftEye = new Graphics();
        leftEye.circle(-eyeSpacing, -radius * 0.1, eyeSize);
        leftEye.fill({ color: 0x000000 });
        leftEye.roundPixels = false;
        eyesContainer.addChild(leftEye);
        
        // Right eye
        const rightEye = new Graphics();
        rightEye.circle(eyeSpacing, -radius * 0.1, eyeSize);
        rightEye.fill({ color: 0x000000 });
        rightEye.roundPixels = false;
        eyesContainer.addChild(rightEye);
        
        container.addChild(eyesContainer);
        
        // Blush left
        const blushLeft = new Graphics();
        blushLeft.circle(-radius * 0.4, radius * 0.3, radius * 0.15);
        blushLeft.fill({ color: 0xFFB6C1, alpha: 0 });
        container.addChild(blushLeft);
        
        // Blush right
        const blushRight = new Graphics();
        blushRight.circle(radius * 0.4, radius * 0.3, radius * 0.15);
        blushRight.fill({ color: 0xFFB6C1, alpha: 0 });
        container.addChild(blushRight);
        
        // Sweat drop (teardrop shape using ellipse)
        const sweatDrop = new Graphics();
        const sweatSize = radius * 0.35;
        // Draw a teardrop shape: ellipse at top, narrowing to point at bottom
        sweatDrop.ellipse(0, -radius - sweatSize * 0.3, sweatSize * 0.4, sweatSize * 0.6);
        sweatDrop.fill({ color: 0x87CEEB, alpha: 0 });
        container.addChild(sweatDrop);
        
        return container;
    }

    /**
     * Updates ball state and visual effects
     */
    public setBallState(state: BallState): void {
        if (!this.ball) return;
        
        this.ball.ballState = state;
        this.ballState = state;
        
        // Reset all effects
        this.ball.blushLeft.alpha = 0;
        this.ball.blushRight.alpha = 0;
        this.ball.sweatDrop.alpha = 0;
        
        if (state === 'aiming') {
            // Show blush and sweat when aiming
            this.ball.blushLeft.alpha = 1;
            this.ball.blushRight.alpha = 1;
            this.ball.sweatDrop.alpha = 1;
        } else if (state === 'idle') {
            // Show sweat when idle/stuck
            this.ball.sweatDrop.alpha = 1;
        }
        // 'normal' state has no effects
    }

    public async createBall(): Promise<void> {
        try {
            this.localBallId = this.socketClient.socket.id;

            const sizeMultiplier = this.upgradeSystem.getSizeMultiplier();
            const currentRadius = BALL_RADIUS * sizeMultiplier;

            const ballContainer = this.createBallGraphics(currentRadius);
            const ballBody = ballContainer.children[0] as Graphics;
            const eyesContainer = ballContainer.children[1] as Container;
            const blushLeft = ballContainer.children[2] as Graphics;
            const blushRight = ballContainer.children[3] as Graphics;
            const sweatDrop = ballContainer.children[4] as Graphics;

            const nameText = new Text({
                text: this.socketClient.name,
                style: new TextStyle({
                    fontFamily: 'Poppins',
                    fontSize: this.isMobile ? 20 : 14,
                    fill: { color: 0xFFFFFF },
                    stroke: { color: 0xAAAAAA, width: 2 }
                })
            });
            nameText.anchor.set(0.5, 1);

            this.gameWorld.addChild(ballContainer);
            this.gameWorld.addChild(nameText);

            this.ball = {
                position: { x: PLAYABLE_AREA / 2 + WALL_THICKNESS, y: CANVAS_HEIGHT - FLOOR_HEIGHT - BALL_RADIUS },
                velocity: { x: 0, y: 0 },
                angle: 0,
                graphics: ballContainer,
                nameText: nameText,
                ballState: 'normal',
                ballBody: ballBody,
                eyesContainer: eyesContainer,
                blushLeft: blushLeft,
                blushRight: blushRight,
                sweatDrop: sweatDrop
            };

            this.matterWorker.postMessage({
                type: 'createBall',
                radius: currentRadius,
                position: this.ball.position,
                collisionFilter: {
                    category: 0x0001,
                    mask: 0x0002
                }
            });

            this.ringGraphics.clear();
            this.ringGraphics.circle(0, 0, BALL_RADIUS + (this.isMobile ? 10 : 5));
            this.ringGraphics.stroke({ width: this.isMobile ? 20 : 4, color: 0xFFFFFF, alpha: 0.5 });
            this.ringGraphics.x = this.ball.position.x;
            this.ringGraphics.y = this.ball.position.y;

            this.soundManager.playPutSound(3);
            this.updateBallPositionOnSocket();
        } catch (error) {
            console.error("error creating ball", error);
        }
    }


    public async createOtherPlayerBall(id: string, playerData: any): Promise<void> {
        if (id === this.localBallId) return;
        if (this.otherBalls[id]) {
            this.removeOtherPlayerBall(id);
        }

        if (!playerData || typeof playerData !== 'object') {
            // console.warn(`PlayerData for ${id} is undefined/invalid in createOtherPlayerBall`);
            return;
        }

        // Fix for invisible players: Ensure position is valid
        let position = playerData.position;
        if (!position || typeof position.x !== 'number' || typeof position.y !== 'number') {
            console.warn(`Invalid position for player ${id}, using default.`);
            position = { x: PLAYABLE_AREA / 2, y: CANVAS_HEIGHT - FLOOR_HEIGHT - BALL_RADIUS };
        }

        const ballColor = this.hexToHex(playerData.ballColor || 'rgb(255, 255, 255)');
        const ballContainer = this.createBallGraphics(BALL_RADIUS, ballColor);

        const nameText = new Text({
            text: playerData.name || 'Unknown',
            style: new TextStyle({
                fontFamily: 'Verdana',
                fontSize: this.isMobile ? 20 : 14,
                fill: { color: 0xFFFFFF },
                stroke: { color: 0xAAAAAA, width: 2 }
            })
        });
        nameText.anchor.set(0.5, 1);
        nameText.rotation = 0; // Keep name text upright

        ballContainer.x = position.x + WALL_THICKNESS;
        ballContainer.y = position.y;
        this.gameWorld.addChild(ballContainer);
        this.gameWorld.addChild(nameText);

        const trailGraphics = new Graphics();
        this.gameWorld.addChild(trailGraphics);

        this.otherBalls[id] = { graphics: ballContainer, trailGraphics, trailPoints: [], nameText: nameText };
        this.previousAndTargetPositions[id] = { previous: position, target: position, lerpTime: 0 };
    }

    public update(delta: number): void {
        this.gameModeHandler.update(delta);

        if (this.ball) {
            this.updateBallGraphics();
            this.updateRingGraphics(delta);
            
            // Update ball state based on game conditions
            if (this.isMouseButtonDown && !this.inMotion && !this.cantHit()) {
                // Player is aiming/dragging
                if (this.ball.ballState !== 'aiming') {
                    this.setBallState('aiming');
                }
            } else if (!this.inMotion && this.cantHit() && this.hasLeftTheGroundSinceLastStrike) {
                // Ball is stuck/waiting (can't hit)
                if (this.ball.ballState !== 'idle') {
                    this.setBallState('idle');
                }
            } else {
                // Normal state
                if (this.ball.ballState !== 'normal') {
                    this.setBallState('normal');
                }
            }
        }
        
        this.trailEffect.update(delta);
        this.impactEffect.update(delta);
        this.powerUpSystem.update(delta);
        
        // Calculate magnet radius
        let magnetRadius = 150 * this.upgradeSystem.getMagnetRadiusMultiplier();
        if (this.powerUpSystem.hasMagnet()) {
            magnetRadius += 300;
        }
        
        this.goldRingManager.update(delta, this.ball?.position, magnetRadius);
        this.goldStarManager.update(delta, this.ball?.position, magnetRadius);
        
        this.portalManager.update(delta);
        this.floatingScoreNumbers.update(delta);
        this.updateComboDisplay(delta);

        this.updateOtherBalls(delta);

        const currentlyHittable = !this.cantHit();
        if (currentlyHittable && !this.previouslyHittable) {
            this.soundManager.playClickSound();
        }
        this.previouslyHittable = currentlyHittable;

        this.updateDebugInfo();
    }

    private updateDebugInfo(): void {
        // Debug text hidden - using new design system
        // Keeping method for potential future use but not updating text
        return;
    }

    private updateBallGraphics(): void {
        if (!this.ball) return;
        this.ball.graphics.x = this.ball.position.x;
        this.ball.graphics.y = this.ball.position.y;
        this.ball.graphics.rotation = this.ball.angle;
        
        // Update player's ball color (recreate fill with new color)
        const ballColor = this.hexToHex(this.socketClient.ballColor);
        const currentRadius = BALL_RADIUS * this.upgradeSystem.getSizeMultiplier();
        this.ball.ballBody.clear();
        this.ball.ballBody.circle(0, 0, currentRadius);
        this.ball.ballBody.fill({ color: ballColor });
        this.ball.ballBody.stroke({ width: 3, color: 0x000000 });
        this.ball.ballBody.roundPixels = false;

        // Eye look animation (subtle movement)
        this.eyeLookTime += 0.016; // Assuming ~60fps
        const eyeOffsetX = Math.sin(this.eyeLookTime * 0.2) * 0.5;
        const eyeOffsetY = Math.cos(this.eyeLookTime * 0.3) * 0.5;
        this.ball.eyesContainer.x = eyeOffsetX;
        this.ball.eyesContainer.y = eyeOffsetY;

        // Update name text position (separate from rotation)
        this.ball.nameText.visible = this.gameModeHandler.shouldShowPlayerLabels();
        if (this.ball.nameText.visible) {
            this.ball.nameText.x = this.ball.position.x;
            this.ball.nameText.y = this.ball.position.y - BALL_RADIUS - 5;
            this.ball.nameText.rotation = 0; // Keep name text upright
        }

        if (this.inMotion) {
            this.trailEffect.emit(this.ball.position.x, this.ball.position.y, this.hexToHex(this.socketClient.ballColor));
        }
    }

    private updateRingGraphics(delta: number): void {
        if (!this.ball) return;
        if (this.cantHit()) {
            this.ringGraphics.clear();
            return;
        }
        this.pulsationTime += delta * 0.1;
        const pulsation = Math.sin(this.pulsationTime) * 2 + (this.isMobile ? 20 : 5);
        this.ringGraphics.clear();
        this.ringGraphics.circle(0, 0, BALL_RADIUS + pulsation);
        this.ringGraphics.stroke({ width: this.isMobile ? 20 : 4, color: 0xFFFF00, alpha: 0.5 });
        this.ringGraphics.x = this.ball.position.x;
        this.ringGraphics.y = this.ball.position.y;
    }

    private updateTrail(): void {
        if (!this.ball) return;
        this.trailPoints.unshift({ x: this.ball.position.x, y: this.ball.position.y });
        if (this.trailPoints.length > 20) this.trailPoints.pop();

        this.trailGraphics.clear();
        for (let i = 0; i < this.trailPoints.length - 1; i++) {
            const alpha = 0.9 - (i / this.trailPoints.length);
            this.trailGraphics.moveTo(this.trailPoints[i].x, this.trailPoints[i].y);
            this.trailGraphics.lineTo(this.trailPoints[i + 1].x, this.trailPoints[i + 1].y);
            this.trailGraphics.stroke({ width: BALL_RADIUS * 2 * (1 - i / this.trailPoints.length), color: 0xFFFFFF, alpha: alpha * 0.5 });
        }
    }

    private updateBallPositionOnSocket(now = false): void {
        if (now || Date.now() - this.lastUpdate < BALL_UPDATE_RATE) return;
        this.lastUpdate = Date.now();
        if (!this.ball) return;
        this.socketClient.updateBallPosition({
            position: { x: this.ball.position.x - WALL_THICKNESS, y: this.ball.position.y },
            inMotion: this.inMotion,
            initialPosition: this.initialPosition,
            highestHeight: this.highestHeight,
            angle: this.ball.angle,
            // we also wanna include this player's scoring data for this round
            scoring: this.gameStateManager.roomState.players[this.socketClient.socket.id].scoring,
        });
    }

    private cantHit(): boolean {
        if (!this.ball || this.gameStateManager.isroundEnded) return true;

        if (this.hasLeftTheGroundSinceLastStrike) {
             // Check if air jumps are even allowed in this mode
             if (!this.gameModeHandler.hasDoubleJump()) {
                 return true; 
             }

             // Check if combo double jump is available first
             if (this.comboDoubleJumpAvailable) {
                 return false; // Allow combo double jump
             }
             
             // Check for extra jumps from power-up
             if (this.extraJumpsRemaining > 0) {
                 return false; // Allow extra jump
             }
             
             // Otherwise check cooldown
             const timeSinceLastPowerUp = Date.now() - this.lastPowerUpUseTime;
             if (timeSinceLastPowerUp > this.POWER_UP_COOLDOWN) {
                 return false; // Allow regular Double Jump
             }
             return true;
        }
        return false;
    }

    public strike(x: number, y: number): void {
        // log all conditions that would prevent a strike
        if (this.cantHit()) return;

        const dx = this.ball.position.x - (x - WALL_THICKNESS);
        const dy = this.ball.position.y - y;
        const angle = Math.atan2(dy, dx);
        const forceOffset = localStorage.getItem('forceOffset') || defaultSettings.player.forceOffset;
        const power = calculateForce(x - WALL_THICKNESS, y, this.ball.position.x, this.ball.position.y, false, Number(forceOffset));

        this.lastStrikePosition = { x: x - WALL_THICKNESS, y: y };
        this.gameModeHandler.onBallStrike(this.ball.position, power, angle);

        if (this.hasLeftTheGroundSinceLastStrike) {
            // This is a Double Jump (Power Up)
            const isComboDoubleJump = this.comboDoubleJumpAvailable;
            
            // Use combo double jump if available
            if (isComboDoubleJump) {
                this.comboDoubleJumpAvailable = false;
                // Don't reset cooldown timer for combo jumps
            } else if (this.extraJumpsRemaining > 0) {
                // Use extra jump from power-up
                this.extraJumpsRemaining--;
            } else {
                // Use regular cooldown jump
                this.lastPowerUpUseTime = Date.now();
            }
            
            this.soundManager.playPowerUpSound(); 
            if (this.confettiEffect && typeof this.confettiEffect.explode === 'function') {
                this.confettiEffect.explode(this.ball.position.x, this.ball.position.y);
            }
            
            // Enhanced power-up impact effect
            const ballColor = this.hexToHex(this.socketClient.ballColor);
            this.impactEffect.createImpact(this.ball.position.x, this.ball.position.y, 2.0, ballColor);
        }

        this.hasLeftTheGroundSinceLastStrike = true;
        const dx2 = this.ball.position.x - (x - WALL_THICKNESS);
        const dy2 = this.ball.position.y - y;
        const distance = Math.sqrt(dx2 * dx2 + dy2 * dy2);
        const forceOffsetVal = localStorage.getItem('forceOffset') || defaultSettings.player.forceOffset;
        let powerVal = calculateForce(x - WALL_THICKNESS, y, this.ball.position.x, this.ball.position.y, false, Number(forceOffsetVal));
        
        // Apply Upgrade Power Multiplier
        powerVal *= this.upgradeSystem.getPowerMultiplier();

        // Apply Game Mode Multiplier (e.g. weaker jumps in Snake Mode)
        if (this.gameModeHandler.getStrikeMultiplier) {
            powerVal *= this.gameModeHandler.getStrikeMultiplier();
        }

        // Enhanced camera shake with power scaling
        const shakeIntensity = powerVal * 50;
        const shakeDuration = 250 + powerVal * 1000;
        cameraShake(this.app.stage, shakeIntensity, shakeDuration);
        
        // Create impact effect at strike point
        const ballColor = this.hexToHex(this.socketClient.ballColor);
        this.impactEffect.createImpact(this.ball.position.x, this.ball.position.y, powerVal * 25, ballColor);
        
        // Apply speed boost multiplier if active
        const speedMultiplier = this.powerUpSystem.getSpeedMultiplier();
        const force = {
            x: (dx2 / distance) * powerVal * speedMultiplier,
            y: (dy2 / distance) * powerVal * speedMultiplier
        };

        this.matterWorker.postMessage({
            type: 'applyForce',
            force: force
        });

        this.inMotion = true;
        this.lastStrikeTime = Date.now();
        this.initialPosition = { x: this.ball.position.x, y: this.ball.position.y };
        this.gameStateManager.roomState.players[this.socketClient.socket.id].scoring.clicks[this.gameStateManager.roomState.round] += 1;
        const ballData: BallUpdate = {
            scoring: this.gameStateManager.roomState.players[this.socketClient.socket.id].scoring,
            roomId: this.socketClient.roomId,
            position: { x: this.ball.position.x - WALL_THICKNESS, y: this.ball.position.y },
            inMotion: true,
            initialPosition: this.initialPosition,
            highestHeight: this.highestHeight,
            strikePosition: { x: x - WALL_THICKNESS, y },
            strikePower: powerVal,
            strike: true,
            angle: 0
        };
        this.socketClient.updateBallPosition(ballData);

        this.soundManager.playPutSound(powerVal);
    }

    public resetBallPosition(): void {
        if (this.ball) {
            const newPosition = {
                x: PLAYABLE_AREA / 2 + WALL_THICKNESS,
                y: (CANVAS_HEIGHT - FLOOR_HEIGHT - BALL_RADIUS)
            };
            this.matterWorker.postMessage({
                type: 'resetBall',
                position: newPosition,
                velocity: { x: 0, y: 0 }
            });
            this.ball.position = newPosition;
            this.ball.velocity = { x: 0, y: 0 };
            this.updateBallGraphics();
            this.trailPoints = [];
            this.trailGraphics.clear();
            this.inMotion = false;
            this.initialPosition = null;
            this.highestHeight = 0;
            // Reset combo when ball resets
            this.comboCount = 0;
            this.comboDoubleJumpAvailable = false;
            this.extraJumpsRemaining = 0;
            this.powerUpSystem.clearAll();
            this.updateBallPositionOnSocket(true);
        }
    }

    public updateOtherPlayerBall(id: string, data: any): void {
        // Skip if this is the local player's ball
        if (id === this.localBallId) return;
        
        this.gameStateManager.updatePlayerFromWebRTCBallUpdate(id, data);

        const otherBall = this.otherBalls[id];
        if (otherBall) {
            advancedInterpolation.updateBallPosition(id, data.position, Date.now());
        } else {
            // Only create if we don't already have this ball
            if (!this.otherBalls[id]) {
                this.createOtherPlayerBall(id, data);
            }
        }

        Object.assign(this.gameStateManager.roomState.players[id], data);
    }

    private updateOtherBalls(delta: number): void {
        Object.entries(this.otherBalls).forEach(([id, ball]) => {
            const playerData = this.gameStateManager.roomState.players[id];
            if (!playerData) return;

            const interpolatedPosition = advancedInterpolation.getInterpolatedPosition(id, Date.now());
            if (interpolatedPosition) {
                ball.graphics.x = interpolatedPosition.x + WALL_THICKNESS;
                ball.graphics.y = interpolatedPosition.y;
                
                // Update name text position (separate from rotation)
                ball.nameText.visible = this.gameModeHandler.shouldShowPlayerLabels();
                if (ball.nameText.visible) {
                    ball.nameText.x = interpolatedPosition.x + WALL_THICKNESS;
                    ball.nameText.y = interpolatedPosition.y - BALL_RADIUS - 5;
                    ball.nameText.rotation = 0; // Keep name text upright
                }
                
                this.updateOtherBallTrail(ball, id);
            }

            // Update ball color
            const ballSprite = ball.graphics.children[0] as Sprite;
            const currentColor = ballSprite.tint;
            const newColor = this.hexToHex(playerData.ballColor);
            if (currentColor !== newColor) {
                ballSprite.tint = newColor;
            }

            // Update player name
            if (ball.nameText.text !== playerData.name) {
                ball.nameText.text = playerData.name;
            }

            // Update ball angle if available
            if (playerData.angle !== undefined) {
                ball.graphics.rotation = playerData.angle;
            }
        });
    }
    private updateOtherBallTrail(ball: OtherBallData, id: string): void {
        ball.trailPoints.unshift({ x: ball.graphics.x, y: ball.graphics.y });
        if (ball.trailPoints.length > 20) ball.trailPoints.pop();

        ball.trailGraphics.clear();
        for (let i = 0; i < ball.trailPoints.length - 1; i++) {
            const alpha = 0.9 - (i / ball.trailPoints.length);
            ball.trailGraphics.moveTo(ball.trailPoints[i].x, ball.trailPoints[i].y);
            ball.trailGraphics.lineTo(ball.trailPoints[i + 1].x, ball.trailPoints[i + 1].y);
            ball.trailGraphics.stroke({ width: BALL_RADIUS * 2 * (1 - i / ball.trailPoints.length), color: 0xFFFFFF, alpha: alpha * 0.5 });
        }
    }

    public removeOtherPlayerBall(id: string): void {
        const otherBall = this.otherBalls[id];
        if (otherBall) {
            this.gameWorld.removeChild(otherBall.graphics);
            this.gameWorld.removeChild(otherBall.trailGraphics);
            this.gameWorld.removeChild(otherBall.nameText);
            delete this.otherBalls[id];
            delete this.previousAndTargetPositions[id];
        }
    }

    private handleCollisionResults(collisions: any[]): void {
        this.colliding = collisions.length > 0;
        if (this.colliding) {
            collisions.forEach(collision => {
                const otherElement = collision.bodyA.label === 'local_ball' ? collision.bodyB : collision.bodyA;
                
                // Create impact effect for platform collisions
                if (this.ball && otherElement.entityId !== SPECIAL_PLATFORM_IDS.FINISH_LINE && 
                    otherElement.entityId !== SPECIAL_PLATFORM_IDS.FLOOR && 
                    otherElement.entityId !== SPECIAL_PLATFORM_IDS.COIN &&
                    otherElement.entityId !== SPECIAL_PLATFORM_IDS.GOLD_RING &&
                    otherElement.entityId !== SPECIAL_PLATFORM_IDS.GOLD_STAR) {
                    const velocity = Math.sqrt(this.ball.velocity.x ** 2 + this.ball.velocity.y ** 2);
                    if (velocity > 2) {
                        const intensity = Math.min(velocity / 10, 1.5);
                        
                        // Grant XP for strong impacts
                        if (velocity > 10 && this.gameModeHandler.hasLevelUps()) {
                            if (this.upgradeSystem.addXp(Math.floor(velocity / 5))) {
                                this.upgradeUI.show(this.upgradeSystem.getAvailableUpgrades());
                                this.socketClient.sendAnalyticsEvent('level_up', { level: this.upgradeSystem.getLevel() });
                            }
                            this.socketClient.sendAnalyticsEvent('xp_gained', { amount: Math.floor(velocity / 5), source: 'impact' });
                        }

                        const ballColor = this.hexToHex(this.socketClient.ballColor);
                        this.impactEffect.createImpact(
                            this.ball.position.x, 
                            this.ball.position.y, 
                            intensity,
                            ballColor
                        );
                        
                        // Trigger platform squash/stretch animation
                        const platform = this.platformManager.platforms.find((p: any) => p.entityId === otherElement.entityId);
                        if (platform && platform.container && typeof platform.container.triggerImpact === 'function') {
                            platform.container.triggerImpact(intensity);
                        }
                        
                        // Update combo
                        this.updateCombo();
                        
                        // Show floating score number with combo multiplier (only if enabled by game mode)
                        if (this.ball && this.gameModeHandler.shouldShowScorePopups()) {
                            const baseScore = 10;
                            const comboMultiplier = Math.max(1, Math.floor(this.comboCount / 3)); // 1x, 2x, 3x, etc.
                            const score = baseScore * comboMultiplier;
                            
                            // Height-based bonus (more score at higher heights)
                            const heightPercent = (CANVAS_HEIGHT - FLOOR_HEIGHT - this.ball.position.y) / (CANVAS_HEIGHT - FLOOR_HEIGHT);
                            const heightBonus = Math.floor(heightPercent * 50); // Up to 50 bonus points
                            const totalScore = score + heightBonus;
                            
                            // Color based on combo multiplier
                            let color = 0xFFFFFF; // White for 1x
                            if (comboMultiplier >= 5) color = 0xFF00FF; // Magenta for 5x+
                            else if (comboMultiplier >= 3) color = 0xFFD700; // Gold for 3x+
                            else if (comboMultiplier >= 2) color = 0x00FF00; // Green for 2x+
                            
                            this.floatingScoreNumbers.showNumber(
                                totalScore,
                                this.ball.position.x,
                                this.ball.position.y - 30,
                                color,
                                comboMultiplier >= 3 ? 28 : 24
                            );
                            
                            // Add score to player
                            const currentRound = this.gameStateManager.roomState.round;
                            const powerUpMultiplier = this.powerUpSystem.getScoreMultiplier();
                            const finalScore = Math.floor(totalScore * powerUpMultiplier);
                            this.gameStateManager.roomState.players[this.socketClient.socket.id].scoring.score[currentRound] += finalScore;
                        }
                    }
                }
                
                switch (otherElement.entityId) {
                    case SPECIAL_PLATFORM_IDS.FINISH_LINE:
                        this.handleFinishLineCollision(otherElement);
                        break;
                    case SPECIAL_PLATFORM_IDS.FLOOR:
                        break;
                    case SPECIAL_PLATFORM_IDS.COIN:
                        this.handleCoinCollision(otherElement.coinIndex);
                        break;
                    case SPECIAL_PLATFORM_IDS.GOLD_RING:
                        this.handleGoldRingCollision(otherElement.ringIndex);
                        break;
                    case SPECIAL_PLATFORM_IDS.GOLD_STAR:
                        this.handleGoldStarCollision(otherElement.starIndex);
                        break;
                    case SPECIAL_PLATFORM_IDS.PORTAL_ENTRY:
                        if (otherElement.portalPairId !== undefined) {
                            this.handlePortalEntryCollision(otherElement.portalPairId);
                        }
                        break;
                    default:
                }
            });

            if (this.ball && Math.sqrt(this.ball.velocity.x ** 2 + this.ball.velocity.y ** 2) > 2.5) {
                this.soundManager.playBounceSound(Math.sqrt(this.ball.velocity.x ** 2 + this.ball.velocity.y ** 2));
            }
        } else {
            this.hasLeftTheGroundSinceLastStrike = true;
        }
    }

    private handleFinishLineCollision(goal: any): void {
        if (this.gameStateManager.getCurrentPlayer().hasHitTheGoal || (!this.isBallStationary() && goal.label === "bucket")) return;
        if (this.gameStateManager.isroundEnded) return;
        
        // Check if we're in campaign mode
        const settings = JSON.parse(localStorage.getItem('golfUpSettings') || '{}');
        const isCampaignMode = settings.roomSettings?.gameType === 'campaign';
        
        if (isCampaignMode) {
            // Campaign mode: show completion modal, don't use timer system
            this.gameStateManager.iHitTheGoal();
            
            // Only emit if soft multiplayer is enabled
            if (settings.roomSettings?.softMultiplayer) {
                this.socketClient.socket.emit('playerFinished', {
                    roomId: this.socketClient.roomId,
                    playerId: this.socketClient.socket.id
                });
            }
            
            this.confettiEffect.start();
            setTimeout(() => {
                this.confettiEffect.stop();
            }, 3000);
            
            // Show campaign completion modal
            if ((window as any).game?.onCampaignLevelComplete) {
                (window as any).game.onCampaignLevelComplete();
            }
        } else {
            // Normal multiplayer mode: use existing timer/round system
            this.gameStateManager.iHitTheGoal();
            this.gameStateManager.isroundEnded = true;

            this.socketClient.socket.emit('playerFinished', {
                roomId: this.socketClient.roomId,
                playerId: this.socketClient.socket.id
            });

            this.confettiEffect.start();
            setTimeout(() => {
                this.confettiEffect.stop();
            }, 3000);
        }
    }

    private setupListeners() {
        this.socketClient.socket.on('playerJoined', (data: any) => {
            if (data && data.id) {
                this.createOtherPlayerBall(data.id, data);
            }
        });
        this.socketClient.socket.on('ballUpdated', this.updateOtherPlayerBall.bind(this));
        this.socketClient.socket.on('playerLeft', this.removeOtherPlayerBall.bind(this));
    }

    private handleCoinCollision(coinIndex: number): void {
        this.platformManager.removeCoin(coinIndex, true);
        
        // XP & Upgrades - Only if allowed by Game Mode
        if (this.gameModeHandler.hasLevelUps()) {
            if (this.upgradeSystem.addXp(10)) {
                this.upgradeUI.show(this.upgradeSystem.getAvailableUpgrades());
                this.soundManager.playVictorySound(); // Level up sound
                this.socketClient.sendAnalyticsEvent('level_up', { level: this.upgradeSystem.getLevel() });
            }
            this.socketClient.sendAnalyticsEvent('xp_gained', { amount: 10, source: 'coin' });
        }

        // Show floating score for coin (only if enabled by game mode)
        if (this.ball && this.gameModeHandler.shouldShowScorePopups()) {
            const baseScore = 5;
            const comboMultiplier = Math.max(1, Math.floor(this.comboCount / 5));
            const powerUpMultiplier = this.powerUpSystem.getScoreMultiplier();
            const totalScore = Math.floor(baseScore * comboMultiplier * powerUpMultiplier);
            
            this.floatingScoreNumbers.showNumber(
                totalScore,
                this.ball.position.x,
                this.ball.position.y - 20,
                0xFFD700, // Gold color
                20
            );
        }
        
        // old central server code
        // this.socketClient.socket.emit('coinCollected', {
        //     roomId: this.socketClient.roomId,
        //     index: coinIndex,
        //     timeStamp: Date.now()
        // });

        // new decentralized code
        this.socketClient.notifyCoinCollected({
            playerId: this.socketClient.socket.id,
            index: coinIndex,
            timeStamp: Date.now()
        });
    }

    private handleGoldRingCollision(ringIndex: number): void {
        // Check if platformManager exists and has the method
        if (!this.platformManager || typeof this.platformManager.removeGoldRing !== 'function') {
            // In some modes like snakeup, gold rings might not be managed by platformManager
            // but we should still try to handle it gracefully
            if (this.goldRingManager) {
                this.goldRingManager.removeRing(ringIndex);
            }
            return;
        }

        const ring = this.goldRingManager.collectRing(ringIndex);
        if (ring) {
            // Random power-up from gold ring
            const powerUpTypes = [
                PowerUpType.SPEED_BOOST,
                PowerUpType.EXTRA_JUMPS,
                PowerUpType.MAGNET,
                PowerUpType.SCORE_MULTIPLIER
            ];
            const randomType = powerUpTypes[Math.floor(Math.random() * powerUpTypes.length)];
            
            this.powerUpSystem.activatePowerUp(randomType);
            
            // If extra jumps, add them
            if (randomType === PowerUpType.EXTRA_JUMPS) {
                this.extraJumpsRemaining += this.powerUpSystem.getExtraJumps();
            }
            
            // Show power-up name
            const powerUpNames = {
                [PowerUpType.SPEED_BOOST]: '⚡ SPEED!',
                [PowerUpType.EXTRA_JUMPS]: '🦘 JUMPS!',
                [PowerUpType.MAGNET]: '🧲 MAGNET!',
                [PowerUpType.SCORE_MULTIPLIER]: '⭐ 2x SCORE!'
            };
            
            if (this.ball) {
                this.floatingScoreNumbers.showNumber(
                    powerUpNames[randomType],
                    ring.x,
                    ring.y,
                    0x00FFFF, // Cyan color
                    28,
                    2000,
                    -60
                );
            }
            
            this.soundManager.playPowerUpSound();
            if (this.confettiEffect && typeof this.confettiEffect.explode === 'function') {
                this.confettiEffect.explode(ring.x, ring.y);
            }
            
            // Remove from physics
            if (this.platformManager && typeof this.platformManager.removeGoldRing === 'function') {
                this.platformManager.removeGoldRing(ringIndex);
            } else {
                // Fallback: Send directly to worker if method missing (shouldn't happen with updated PlatformManager)
                this.matterWorker.postMessage({
                    type: 'removeGoldRing',
                    index: ringIndex
                });
            }
        }
    }

    private handleGoldStarCollision(starIndex: number): void {
        // Check if platformManager exists and has the method
        if (!this.platformManager || typeof this.platformManager.removeGoldStar !== 'function') {
            if (this.goldStarManager) {
                this.goldStarManager.removeStar(starIndex);
            }
            return;
        }

        const star = this.goldStarManager.collectStar(starIndex);
        if (star) {
            // Grant XP
            if (this.gameModeHandler.hasLevelUps()) {
                if (this.upgradeSystem.addXp(50)) {
                    this.upgradeUI.show(this.upgradeSystem.getAvailableUpgrades());
                    this.soundManager.playVictorySound();
                    this.socketClient.sendAnalyticsEvent('level_up', { level: this.upgradeSystem.getLevel() });
                }
                this.socketClient.sendAnalyticsEvent('xp_gained', { amount: 50, source: 'star' });
            }

            // Gold stars give score bonus with height-based escalation
            const currentRound = this.gameStateManager.roomState.round;
            const baseScore = 100;
            
            // Height-based escalation: more score at higher heights
            let heightBonus = 0;
            if (this.ball) {
                const heightPercent = (CANVAS_HEIGHT - FLOOR_HEIGHT - this.ball.position.y) / (CANVAS_HEIGHT - FLOOR_HEIGHT);
                heightBonus = Math.floor(heightPercent * 200); // Up to 200 bonus points
            }
            
            // Combo multiplier
            const comboMultiplier = Math.max(1, Math.floor(this.comboCount / 5));
            const powerUpMultiplier = this.powerUpSystem.getScoreMultiplier();
            
            const totalScore = Math.floor((baseScore + heightBonus) * comboMultiplier * powerUpMultiplier);
            this.gameStateManager.roomState.players[this.socketClient.socket.id].scoring.score[currentRound] += totalScore;
            
            // Show floating score number (only if enabled by game mode)
            if (this.ball && this.gameModeHandler.shouldShowScorePopups()) {
                this.floatingScoreNumbers.showNumber(
                    totalScore,
                    star.x,
                    star.y,
                    0xFFD700, // Gold color
                    32,
                    2000,
                    -60
                );
            }
            
            this.soundManager.playPowerUpSound();
            if (this.confettiEffect && typeof this.confettiEffect.explode === 'function') {
                this.confettiEffect.explode(star.x, star.y);
            }
            
            // Remove from physics
            if (this.platformManager && typeof this.platformManager.removeGoldStar === 'function') {
                this.platformManager.removeGoldStar(starIndex);
            } else {
                this.matterWorker.postMessage({
                    type: 'removeGoldStar',
                    index: starIndex
                });
            }
        }
    }

    private handlePortalEntryCollision(portalPairId: number): void {
        const now = Date.now();
        
        // Check cooldown to prevent rapid teleporting
        if (now - this.lastPortalTeleportTime < this.PORTAL_COOLDOWN) {
            return;
        }
        
        if (!this.ball) return;
        
        // Find the exit portal
        const exitPortal = this.portalManager.getExitPortal(portalPairId);
        if (!exitPortal) {
            console.warn(`No exit portal found for pair ${portalPairId}`);
            return;
        }
        
        // Preserve momentum - get current velocity
        const currentVelocity = { ...this.ball.velocity };
        const speed = Math.sqrt(currentVelocity.x ** 2 + currentVelocity.y ** 2);
        
        // Teleport ball to exit portal position
        const newPosition = {
            x: exitPortal.x,
            y: exitPortal.y
        };
        
        // Send teleport command to physics worker
        this.matterWorker.postMessage({
            type: 'teleportBall',
            position: newPosition,
            velocity: currentVelocity // Preserve velocity
        });
        
        // Update local ball position immediately for smooth visual
        this.ball.position = newPosition;
        this.updateBallGraphics();
        
        // Visual effects
        if (this.confettiEffect && typeof this.confettiEffect.explode === 'function') {
            this.confettiEffect.explode(exitPortal.x, exitPortal.y);
        }
        
        // Show portal effect text
        this.floatingScoreNumbers.showNumber(
            'PORTAL!',
            exitPortal.x,
            exitPortal.y - 30,
            0x9370DB, // Purple color
            24,
            1000,
            -40
        );
        
        // Play sound
        this.soundManager.playPowerUpSound();
        
        // Update cooldown
        this.lastPortalTeleportTime = now;
    }

    private updateHighestHeight(): void {
        if (!this.ball) return;
        const height = CANVAS_HEIGHT - FLOOR_HEIGHT - BALL_RADIUS - this.ball.position.y;
        if (height > this.highestHeight) {
            this.highestHeight = height;
        }
    }

    private isBallStationary(): boolean {
        if (!this.ball) return true;
        const velocityThreshold = 0.1;
        const isSlow = Math.abs(this.ball.velocity.x) < velocityThreshold &&
            Math.abs(this.ball.velocity.y) < velocityThreshold;
        
        // Only consider stationary if slow AND colliding with something (not mid-air)
        return isSlow && this.colliding;
    }

    public isBallTouchingPlatform(): boolean {
        if (!this.ball) return false;
        if (this.ball.position.y + BALL_RADIUS >= CANVAS_HEIGHT - FLOOR_HEIGHT) return true;

        this.matterWorker.postMessage({
            type: 'isBallTouchingPlatform',
            platforms: this.platformManager.platforms
        });
        return false;
    }

    public removeAllBalls(): void {
        if (this.ball) {
            this.matterWorker.postMessage({ type: 'removeBall' });
            this.gameWorld.removeChild(this.ball.graphics);
            this.gameWorld.removeChild(this.ball.nameText);
            this.ball = null;
        }

        Object.values(this.otherBalls).forEach(otherBall => {
            this.gameWorld.removeChild(otherBall.graphics);
            this.gameWorld.removeChild(otherBall.trailGraphics);
            this.gameWorld.removeChild(otherBall.nameText);
        });

        this.otherBalls = {};
        this.previousAndTargetPositions = {};
        this.trailPoints = [];
        this.trailGraphics.clear();
    }

    private hexToHex(stringFormat: string): number {
        return parseInt(stringFormat.replace('#', '0x'));
    }

    public getPosition(): { x: number, y: number } | null {
        return this.ball?.position ? { ...this.ball?.position } : null;
    }

    public getPowerUpCooldownProgress(): number {
        const timeSince = Date.now() - this.lastPowerUpUseTime;
        return Math.min(timeSince / this.POWER_UP_COOLDOWN, 1);
    }

    public setMouseButtonDown(isDown: boolean): void {
        this.isMouseButtonDown = isDown;
    }

    public isMouseDown(): boolean {
        return this.isMouseButtonDown;
    }

    public handleRightClick(worldX: number, worldY: number): void {
        this.gameModeHandler.onRightClick(worldX, worldY);
    }

    private updateCombo(): void {
        if (!this.gameModeHandler.hasCombos()) return;
        const now = Date.now();
        if (now - this.lastHitTime < this.COMBO_TIMEOUT) {
            this.comboCount++;
            
            // Grant combo double jump when threshold is reached
            if (this.comboCount >= this.COMBO_THRESHOLD && !this.comboDoubleJumpAvailable) {
                this.comboDoubleJumpAvailable = true;
                this.soundManager.playPowerUpSound();
                
                // Visual feedback for earning combo double jump
                if (this.ball && this.confettiEffect && typeof this.confettiEffect.explode === 'function') {
                    this.confettiEffect.explode(this.ball.position.x, this.ball.position.y);
                }
            }
        } else {
            this.comboCount = 1;
            // Reset combo double jump if combo times out
            this.comboDoubleJumpAvailable = false;
        }
        this.lastHitTime = now;
    }

    private updateComboDisplay(delta: number): void {
        const now = Date.now();
        const timeSinceLastHit = now - this.lastHitTime;
        
        if (this.comboCount > 1 && timeSinceLastHit < this.COMBO_TIMEOUT) {
            // Show combo
            if (!this.comboText) {
                this.comboText = new Text({
                    text: '',
                    style: new TextStyle({
                        fontFamily: 'Arial',
                        fontSize: 32,
                        fill: 0xFFFFFF,
                        stroke: { color: 0x000000, width: 4 },
                        fontWeight: 'bold'
                    })
                });
                this.comboText.anchor.set(0.5);
                this.gameWorld.addChild(this.comboText);
            }
            
            if (this.ball) {
                this.comboText.x = this.ball.position.x;
                this.comboText.y = this.ball.position.y - 50;
                
                // Show different text if combo double jump is available
                if (this.comboDoubleJumpAvailable) {
                    this.comboText.text = `${this.comboCount}x COMBO!\nDOUBLE JUMP READY!`;
                    this.comboText.style.fill = 0x00FF00; // Green when ready
                } else {
                    this.comboText.text = `${this.comboCount}x COMBO!`;
                    this.comboText.style.fill = 0xFFFFFF; // White normally
                }
                
                // Pulse effect (stronger when double jump is ready)
                const pulseIntensity = this.comboDoubleJumpAvailable ? 0.3 : 0.2;
                const pulse = 1 + Math.sin(Date.now() * 0.01) * pulseIntensity;
                this.comboText.scale.set(pulse);
                
                // Fade based on time remaining
                const timeRemaining = (this.COMBO_TIMEOUT - timeSinceLastHit) / this.COMBO_TIMEOUT;
                this.comboText.alpha = timeRemaining;
                this.comboText.visible = true;
            }
        } else {
            // Hide combo
            if (this.comboText) {
                this.comboText.visible = false;
            }
            // Only reset combo count if it's fully timed out
            if (timeSinceLastHit >= this.COMBO_TIMEOUT) {
                this.comboCount = 0;
                this.comboDoubleJumpAvailable = false;
            }
        }
    }
}
